import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Attachment} from '../../../../../../../backend/interfaces/attachment';
import {useUsersInfoCache} from '../../../../../../../hooks/use-users-info-cache';
import {EntityUserAttachmentsRepository} from '../../../../../../../repositories/entity-user-attachments';

export function useSeniorAttachmentsData() {
  const {id, entityId} = useParams<{id?: string; entityId?: string}>();
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<any | null>(null);

  // Attachment authors info cache
  const {users: authorsCache, loading: authorsCacheLoading} =
    useSeniorAttachmentsAuthorsCache(attachments);

  const loading =
    // Loading means:
    // Fetching attachments data
    fetchLoading ||
    // or we are loading authors cache for first time (empty cache)
    (authorsCacheLoading && Object.keys(authorsCache).length === 0);

  const fetch = useCallback(async () => {
    if (!entityId) throw new Error('Entity ID is required to load attachments.');
    if (!id) throw new Error('Senior ID is required to load attachments.');

    setFetchLoading(true);
    setError(null);
    try {
      const attachments = await EntityUserAttachmentsRepository.instance.getAttachments(
        entityId,
        id
      );
      setAttachments(attachments);

      return attachments;
    } catch (error) {
      setError(error);
    } finally {
      setFetchLoading(false);
    }
  }, [entityId, id]);

  const refresh = (attachments: Attachment[]) => {
    setAttachments(attachments);
  };

  const refreshAttachment = (attachment: Attachment) => {
    setAttachments((prevAttachments) =>
      prevAttachments.map((a) => (a.id === attachment.id ? attachment : a))
    );
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((attachment) => attachment.id !== attachmentId)
    );
  };

  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    loading,
    error,
    attachments,
    authorsCache,
    /** Fetch for new attachments data. */
    fetch,
    /** Refresh current attachments with new data. (Does not fetch) */
    refresh,
    /** Refresh given attachment (Does not fetch) */
    refreshAttachment,
    /** Remove attachment from the list */
    removeAttachment,
  };
}

function useSeniorAttachmentsAuthorsCache(attachments: Attachment[]) {
  const usersIds = useMemo(
    () => attachments.map((attachment) => attachment.authorUserId),
    [attachments]
  );

  return useUsersInfoCache(usersIds);
}
